/**
 * Mapeo de la respuesta de `pre-initiate` → planes de cuotas de la UI.
 * Cada tarjeta trae un grupo de `installmentOptions` por monto calculado (el total y el monto
 * propio en pago dividido); se usa el grupo cuyo `value` coincide con el monto de la tarjeta.
 */
import type { InstallmentPlan } from '../components/InstallmentSelector';
import type { InstallmentOptionGroup, PreInitiatePayment } from '../domain/models';
import { translate } from '../i18n';
import { roundMoneyAmount } from '../utils/moneyAmount';

const AMOUNT_TOLERANCE = 0.01;

export const pickOptionGroup = (
  payment: PreInitiatePayment,
  amount: number,
): InstallmentOptionGroup | undefined => {
  if (!payment.installmentOptions.length) {
    return undefined;
  }
  const exact = payment.installmentOptions.find(
    (group) => Math.abs(group.value - amount) < AMOUNT_TOLERANCE,
  );
  if (exact) {
    return exact;
  }
  const closest = payment.installmentOptions.reduce((best, group) => {
    const distance = Math.abs(group.value - amount);
    if (!best || distance < best.distance) {
      return { group, distance };
    }
    return best;
  }, null as { group: InstallmentOptionGroup; distance: number } | null);
  return closest?.group ?? payment.installmentOptions[0];
};

/**
 * Monto de financiación (pesos) para el resumen del host: total del plan elegido menos el payable
 * en 1 cuota en el mismo contexto de simulación. Tras `installment-promo/sync` la grilla puede
 * conservar 1 cuota del routing; `simulatedOneCuotaTotal` viene de la respuesta completa del sync.
 */
export const computeInstallmentFinancingInterest = (
  plans: InstallmentPlan[],
  selectedId: string | undefined,
  options?: { fallbackBase?: number; simulatedOneCuotaTotal?: number },
): number => {
  const selected = plans.find((plan) => plan.id === selectedId);
  if (!selected || selected.months <= 1) {
    return 0;
  }
  if (!selected.interestRate && !selected.rates) {
    return 0;
  }
  const gridOneCuota = plans.find((plan) => plan.months === 1)?.totalAmount;
  const base = options?.simulatedOneCuotaTotal ?? gridOneCuota ?? options?.fallbackBase;
  if (typeof base !== 'number') {
    return 0;
  }
  return Math.max(0, roundMoneyAmount(selected.totalAmount - base));
};

export const toInstallmentPlans = (
  payment: PreInitiatePayment | undefined,
  amount: number,
): InstallmentPlan[] => {
  const group = payment ? pickOptionGroup(payment, amount) : undefined;
  if (!group) {
    return [];
  }

  return group.installments.map((installment) => {
    const interest = installment.total - group.value;
    const hasInterest = interest > AMOUNT_TOLERANCE;
    const percent = Math.round((interest / group.value) * 100);

    return {
      id: String(installment.count),
      months: installment.count,
      monthlyAmount: installment.value,
      totalAmount: installment.total,
      ...(hasInterest && percent > 0
        ? { interestRate: translate('paymentsUI.installments.interestLabel', { percent }) }
        : {}),
      ...(hasInterest &&
      typeof installment.cft === 'number' &&
      typeof installment.tea === 'number' &&
      typeof installment.tna === 'number'
        ? { rates: { cft: installment.cft, tea: installment.tea, tna: installment.tna } }
        : {}),
      ...(!hasInterest && installment.count > 1
        ? {
            badge: {
              text: translate('paymentsUI.installments.noInterestBadge'),
              variant: 'success' as const,
            },
          }
        : {}),
    };
  });
};
